/**
 * CSRF-aware + Auth-aware fetch wrapper.
 * Automatically injects:
 * - Authorization: Bearer <token> (when authenticated)
 * - x-csrf-token (on mutations)
 *
 * Retry logic:
 * - 401 -> refresh Supabase session, retry once
 * - 403 "Invalid CSRF token" -> refresh CSRF token, retry once
 */

import { getAccessToken, setStoredAccessToken } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

let csrfToken: string | null = null;

/**
 * Initialize the CSRF token by fetching from the backend.
 * Called after login completes via useCsrfToken hook.
 */
export async function initCsrfToken(): Promise<void> {
  try {
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/csrf-token', {
      credentials: 'include',
      headers,
    });
    if (res.ok) {
      const data = await res.json();
      csrfToken = data.csrfToken;
    } else {
      console.warn('Failed to fetch CSRF token:', res.status);
    }
  } catch (err) {
    console.warn('CSRF token fetch failed:', err);
  }
}

function buildHeaders(init?: RequestInit): Record<string, string> {
  const headerObj: Record<string, string> = {};
  const existing = init?.headers;
  if (existing instanceof Headers) {
    existing.forEach((v, k) => { headerObj[k] = v; });
  } else if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    Object.assign(headerObj, existing);
  }
  return headerObj;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Fetch wrapper that automatically includes auth token and CSRF token.
 * Drop-in replacement for fetch() — same signature.
 */
export async function fetchWithCsrf(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const isMutation = MUTATION_METHODS.has(method);

  // Build headers with auth + CSRF
  const headerObj = buildHeaders(init);
  const accessToken = getAccessToken();
  if (accessToken) {
    headerObj['Authorization'] = `Bearer ${accessToken}`;
  }
  if (isMutation && csrfToken) {
    headerObj['x-csrf-token'] = csrfToken;
  }
  init = { ...init, headers: headerObj, credentials: 'include' };

  let response = await fetch(input, init);

  // 401 -> refresh Supabase session and retry once
  if (response.status === 401) {
    const { data: { session } } = await supabase.auth.refreshSession();
    if (session) {
      setStoredAccessToken(session.access_token);
      headerObj['Authorization'] = `Bearer ${session.access_token}`;
      init = { ...init, headers: headerObj };
      response = await fetch(input, init);
    }
  }

  // 403 with CSRF error -> refresh CSRF token and retry once
  if (response.status === 403 && isMutation) {
    try {
      const errorBody = await response.clone().json();
      if (errorBody?.error === 'Invalid CSRF token') {
        await initCsrfToken();
        if (csrfToken) {
          headerObj['x-csrf-token'] = csrfToken;
          init = { ...init, headers: headerObj };
          return fetch(input, init);
        }
      }
    } catch {
      // Response wasn't JSON — return original response
    }
  }

  return response;
}
